import * as React from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import CervinoText from "../typography/CervinoText";

const LOADED_SRC_CACHE = new Set();

export default function ShimmerImage({
                                         src,
                                         alt,
                                         width = "100%",
                                         height = "100%",
                                         borderRadius = 0,
                                         objectFit = "contain",
                                         showBorder = false,
                                         sx = {},
                                     }) {
    const theme = useTheme();
    const [loaded, setLoaded] = React.useState(false);
    const [errored, setErrored] = React.useState(false);

    React.useEffect(() => {
        setErrored(false);

        if (!src) {
            setLoaded(false);
            setErrored(true);
            return;
        }

        // ✅ If we've seen this src loaded before, skip shimmer forever
        if (LOADED_SRC_CACHE.has(src)) {
            setLoaded(true);
            return;
        }

        setLoaded(false);

        const img = new Image();
        img.src = src;

        const handleLoad = () => {
            LOADED_SRC_CACHE.add(src);
            setLoaded(true);
        };

        const handleError = () => setErrored(true);

        if (img.complete && img.naturalWidth) {
            handleLoad();
            return;
        }

        img.addEventListener("load", handleLoad);
        img.addEventListener("error", handleError);

        return () => {
            img.removeEventListener("load", handleLoad);
            img.removeEventListener("error", handleError);
        };
    }, [src]);

    return (
        <Box
            sx={{
                position: "relative",
                width,
                height,
                borderRadius,
                overflow: "hidden", // clips shimmer
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...sx,
            }}
        >
            {/* IMAGE (does NOT fill container when contain => border hugs image) */}
            {!errored && !!src && (
                <Box
                    component="img"
                    src={src}
                    alt={alt}
                    sx={{
                        display: "block",

                        ...(objectFit === "contain"
                            ? {
                                maxWidth: "100%",
                                maxHeight: "100%",
                                width: "auto",
                                height: "auto",
                            }
                            : {
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }),

                        borderRadius,
                        border: showBorder ? `1px solid ${theme.palette.primary.main}` : "none",
                    }}
                />
            )}

            {/* SHIMMER OVERLAY (covers container, not the image border) */}
            {!loaded && !errored && (
                <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        sx={{
                            width: "100%",
                            height: "100%",
                            borderRadius,
                            transform: "none",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius,
                            overflow: "hidden",
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "-40%",
                                width: "40%",
                                height: "100%",
                                background:
                                    "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.4) 50%, rgba(255,255,255,0) 100%)",
                                animation: "shimmer-slide 1.1s ease-in-out infinite",
                            },
                            "@keyframes shimmer-slide": {
                                "0%": { transform: "translateX(0)" },
                                "100%": { transform: "translateX(250%)" },
                            },
                        }}
                    />
                </Box>
            )}

            {/* FALLBACK */}
            {errored && (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius,
                        border: `1px dashed ${theme.palette.primary.main}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 1,
                    }}
                >
                    <CervinoText sx={{ fontSize: 12, color: theme.palette.primary.main, textAlign: "center" }}>
                        Image unavailable
                    </CervinoText>
                </Box>
            )}
        </Box>
    );
}
